import React from 'react';
import NodeForm from './node_form';
import RichContentManager from './RichContentManager';

const EditPanel = ({ 
  selectedNode, 
  editingNode, 
  onEdit, 
  onSave, 
  onDelete, 
  onCancel, 
  onAddChild 
}) => {
  const [showRichContent, setShowRichContent] = React.useState(false);

  React.useEffect(() => {
    setShowRichContent(false);
  }, [selectedNode?.id]);
  
  
  console.log('EditPanel props received:', {
    selectedNode,
    editingNode,
    editingNodeType: typeof editingNode,
    editingNodeKeys: editingNode ? Object.keys(editingNode) : 'null',
    isEditing: !!editingNode
  });

  // Get available child types for a node
  const getAvailableChildTypes = (nodeType) => {
    switch (nodeType) {
      case 'topic': return ['subtopic', 'chunk'];
      case 'subtopic': return ['chunk'];
      case 'chunk': return ['micro_chunk'];
      case 'micro_chunk': return ['explanation'];
      default: return [];
    }
  };

  // Get node type display name
  const getNodeTypeDisplay = (nodeType) => {
    switch (nodeType) {
      case 'topic': return 'Topic';
      case 'subtopic': return 'Subtopic';
      case 'chunk': return 'Chunk';
      case 'micro_chunk': return 'Micro Chunk';
      case 'explanation': return 'Explanation';
      default: return nodeType;
    }
  };

  // Get node icon
  const getNodeIcon = (nodeType) => {
    switch (nodeType) {
      case 'topic': return '📖';
      case 'subtopic': return '📝';
      case 'chunk': return '📄';
      case 'micro_chunk': return '🔍';
      case 'explanation': return '💡';
      default: return '📌';
    }
  };

  // Build breadcrumb path
  const getBreadcrumb = (node) => {
    if (!node) return [];
    
    const breadcrumb = [];
    
    // Add node type hierarchy based on level
    if (node.level >= 0) breadcrumb.push({ name: 'Chapter', type: 'chapter' });
    if (node.level >= 1) breadcrumb.push({ name: 'Topic', type: 'topic' });
    if (node.level >= 2) breadcrumb.push({ name: 'Subtopic', type: 'subtopic' });
    if (node.level >= 3) breadcrumb.push({ name: 'Chunk', type: 'chunk' });
    if (node.level >= 4) breadcrumb.push({ name: 'Micro Chunk', type: 'micro_chunk' });
    if (node.level >= 5) breadcrumb.push({ name: 'Explanation', type: 'explanation' });
    
    return breadcrumb;
  };

  // Render node preview (view mode)
  const renderNodePreview = (node) => {
    return (
      <div className="node-preview">
        {/* Preview Header */}
        <div className="preview-header">
          <div className="preview-title">
            <span className="preview-icon">{getNodeIcon(node.node_type)}</span>
            <h3>{node.name}</h3>
          </div>
          <div className="preview-actions">
            <button className="btn btn-primary" onClick={() => onEdit(node)}>
              ✏️ Edit
            </button>
            <button className="btn btn-danger" onClick={() => onDelete(node)}>
              🗑️ Delete
            </button>
            {(node.node_type === 'micro_chunk' || node.node_type === 'explanation') && (
              <button
                className="btn btn-primary"
                onClick={() => setShowRichContent(true)}
              >
                📊 Manage Rich Content
              </button>
            )}
          </div>
        </div>

        <div className="preview-content">
          {/* Basic Information */}
          <div className="preview-section">
            <h4>Basic Information</h4>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              <div className="preview-field">
                <label>Type:</label>
                <span className="preview-type">{getNodeTypeDisplay(node.node_type)}</span>
              </div>
              <div className="preview-field">
                <label>Name:</label>
                <span>{node.name}</span>
              </div>
              <div className="preview-field">
                <label>Order:</label>
                <span>{node.order_index}</span>
              </div>
              <div className="preview-field">
                <label>ID:</label>
                <span>{node.id}</span>
              </div>
            </div>
          </div>

          {/* Description */}
          {node.description && (
            <div className="preview-section">
              <h4>Description</h4>
              <div className="preview-text">{node.description}</div>
            </div>
          )}

          {/* English Content */}
          {(node.text_en || node.content_en) && (
            <div className="preview-section">
              <h4>English Content</h4>
              <div className="preview-text">{node.text_en || node.content_en}</div>
            </div>
          )}

          {/* Hindi Content */}
          {(node.text_hi || node.content_hi) && (
            <div className="preview-section">
              <h4>Hindi Content</h4>
              <div className="preview-text">{node.text_hi || node.content_hi}</div>
            </div>
          )}

          {/* Meta Tag */}
          {node.meta_tag && (
            <div className="preview-section">
              <h4>Meta Tag</h4>
              <div className="preview-field">
                <span>{node.meta_tag}</span>
              </div>
            </div>
          )}

          {/* Image */}
          {node.image_path && (
            <div className="preview-section">
              <h4>Image</h4>
              <div className="preview-field">
                <label>Path:</label>
                <span>{node.image_path}</span>
              </div>
            </div>
          )}

          {/* Children summary */}
          {node.children && node.children.length > 0 && (
            <div className="preview-section">
              <h4>Children ({node.children.length})</h4>
              <div className="children-summary">
                {node.children.map(child => (
                  <div key={child.id} className="child-item">
                    <span className="child-icon">{getNodeIcon(child.node_type)}</span>
                    <span>{child.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Add Child Section */}
        {getAvailableChildTypes(node.node_type).length > 0 && (
          <div className="add-child-section">
            <h4>Add Child Content</h4>
            <div className="add-child-buttons">
              {getAvailableChildTypes(node.node_type).map(childType => (
                <button
                  key={childType}
                  className="btn btn-success"
                  onClick={() => onAddChild(node, childType)}
                >
                  ➕ Add {getNodeTypeDisplay(childType)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };
  return (
    <div className="edit-panel">
      {/* Panel Header - only show when viewing (not editing) */}
      {!editingNode && (
        <div className="panel-header">
          <div className="panel-header-content">
            {/* Breadcrumb */}
            {selectedNode && (
              <div className="breadcrumb">
                {getBreadcrumb(selectedNode).map((crumb, index) => (
                  <span key={index} className={`breadcrumb-item ${crumb.current ? 'current' : ''}`}>
                    {index > 0 && <span className="breadcrumb-separator">›</span>}
                    {crumb.name}
                  </span>
                ))}
              </div>
            )}
            
            <h2>
              {selectedNode ? (
                <>
                  👁️ Viewing: {getNodeTypeDisplay(selectedNode.node_type)}
                </>
              ) : (
                '📝 Content Editor'
              )}
            </h2>
          </div>
        </div>
      )}
      {/* Panel Content */}
      <div className="panel-content">
        {editingNode ? (
            // Edit Mode - Show Form
            <NodeForm
              node={editingNode}
              onSave={onSave}
              onCancel={onCancel}
            />
          ) : showRichContent && selectedNode ? (
            // Rich Content Manager - no padding wrapper needed
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', margin: '-2rem' }}>
              <RichContentManager
                node={selectedNode}
                onBack={() => setShowRichContent(false)}
              />
            </div>
          ) : selectedNode ? (
            // View Mode - Show Preview
            renderNodePreview(selectedNode)
          ) : (
          // No Selection - Welcome State
          <div className="welcome-state">
            <div className="welcome-content">
              <h3>👋 Welcome to Content Editor</h3>
              <p>Select a content item from the tree on the left to view or edit it.</p>
              
              <div className="welcome-tips">
                <h4>💡 Quick Tips:</h4>
                <ul>
                  <li>🔍 Use the search box to find specific content</li>
                  <li>➕ Click the green plus buttons to add new content</li>
                  <li>✏️ Click "Edit" to modify selected content</li>
                  <li>🗑️ Click "Delete" to remove content (with confirmation)</li>
                </ul>
              </div>

              <div className="welcome-hierarchy">
                <h4>📚 Content Hierarchy:</h4>
                <div className="hierarchy-flow">
                  <div className="hierarchy-item">📖 Topics</div>
                  <div className="hierarchy-arrow">↓</div>
                  <div className="hierarchy-item">📝 Subtopics</div>
                  <div className="hierarchy-arrow">↓</div>
                  <div className="hierarchy-item">📄 Chunks</div>
                  <div className="hierarchy-arrow">↓</div>
                  <div className="hierarchy-item">🔍 Micro Chunks</div>
                  <div className="hierarchy-arrow">↓</div>
                  <div className="hierarchy-item">💡 Explanations</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditPanel;